import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
    personalization: boolean;
}

const CookieConsent: React.FC = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true, // Always true
        analytics: true,
        marketing: true,
        personalization: true
    });

    useEffect(() => {
        const consent = localStorage.getItem('cookie_consent');
        if (!consent) {
            // Slight delay so it doesn't pop in instantly and jar the user
            const timer = setTimeout(() => setShowBanner(true), 1500);
            return () => clearTimeout(timer);
        } else {
            // Already consented
            try {
                const parsed = JSON.parse(consent);
                setPreferences(parsed);
            } catch (e) { /* ignore */ }
        }
    }, []);

    const savePreferences = (prefs: CookiePreferences) => {
        localStorage.setItem('cookie_consent', JSON.stringify(prefs));
        setShowBanner(false);
        setShowModal(false);
    };

    const handleAcceptAll = () => {
        const prefs = {
            necessary: true,
            analytics: true,
            marketing: true,
            personalization: true
        };
        savePreferences(prefs);
    };

    const handleRejectAll = () => {
        const prefs = {
            necessary: true,
            analytics: false,
            marketing: false,
            personalization: false
        };
        savePreferences(prefs);
    };

    const handleSaveMyChoices = () => {
        savePreferences(preferences);
    };

    return (
        <>
            <AnimatePresence>
                {showBanner && (
                    <motion.div
                        className="cc-banner"
                        initial={{ y: "150%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "150%", opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                    >
                        <div className="cc-content">
                            <div className="cc-text">
                                <h4>We Value Your Privacy</h4>
                                <p>
                                    We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking <strong>"Accept All"</strong>, you consent to our use of cookies.
                                    <Link to="/cookies-policy" className="cc-learn-more">Learn More</Link>
                                </p>
                            </div>
                            <div className="cc-actions">
                                <button className="cc-btn cc-btn-outline" onClick={() => { setShowBanner(false); setShowModal(true); }}>Customize</button>
                                <button className="cc-btn cc-btn-secondary" onClick={handleRejectAll}>Reject All</button>
                                <button className="cc-btn cc-btn-primary" onClick={handleAcceptAll}>Accept All</button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <Dialog
                header="Customize Cookie Preferences"
                visible={showModal}
                className="cc-modal-dialog"
                onHide={() => { setShowModal(false); setShowBanner(true); }}
                footer={
                    <div className="cc-modal-footer">
                        <button className="cc-btn cc-btn-outline" onClick={() => { setShowModal(false); setShowBanner(true); }}>Cancel</button>
                        <button className="cc-btn cc-btn-primary" onClick={handleSaveMyChoices}>Save My Choices</button>
                    </div>
                }
            >
                <div className="cc-modal-body">
                    <p className="cc-modal-desc">
                        Manage your cookie preferences below. You can change your choices at any time. Essential cookies cannot be disabled as they are required for the website to function properly.
                    </p>

                    <div className="cc-pref-item">
                        <div className="cc-pref-info">
                            <h5>Strictly Necessary</h5>
                            <p>Required for the website to function properly. Cannot be disabled.</p>
                        </div>
                        <div className="cc-pref-action">
                            <InputSwitch checked={preferences.necessary} disabled />
                        </div>
                    </div>

                    <div className="cc-pref-item">
                        <div className="cc-pref-info">
                            <h5>Analytics Cookies</h5>
                            <p>Help us understand how visitors interact with our website by collecting and reporting information anonymously.</p>
                        </div>
                        <div className="cc-pref-action">
                            <InputSwitch checked={preferences.analytics} onChange={(e) => setPreferences({ ...preferences, analytics: e.value as boolean })} />
                        </div>
                    </div>

                    <div className="cc-pref-item">
                        <div className="cc-pref-info">
                            <h5>Marketing Cookies</h5>
                            <p>Used to track visitors across websites. The intention is to display ads that are relevant and engaging for the user.</p>
                        </div>
                        <div className="cc-pref-action">
                            <InputSwitch checked={preferences.marketing} onChange={(e) => setPreferences({ ...preferences, marketing: e.value as boolean })} />
                        </div>
                    </div>

                    <div className="cc-pref-item">
                        <div className="cc-pref-info">
                            <h5>Personalization Cookies</h5>
                            <p>Allow the website to remember choices you make and provide enhanced, more personal features.</p>
                        </div>
                        <div className="cc-pref-action">
                            <InputSwitch checked={preferences.personalization} onChange={(e) => setPreferences({ ...preferences, personalization: e.value as boolean })} />
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default CookieConsent;

